import React, {createContext, FC, useCallback, useContext, useMemo} from 'react'
import {WithChildren} from '../../../../_metronic/helpers'
import {
  ProcurementEffectivePermissionsDto,
  ProcurementPermissionScope,
  ProcurementResolvedPermissionDto,
} from './_models'
import {getRequiredPermissionsForProcurementRoute, ProcurementPermissionCode, expandProcurementPermissionCandidates} from './permissions'
import {useAuth} from '../../auth'

type ProcurementPermissionMap = Record<string, ProcurementResolvedPermissionDto>

type ProcurementPermissionContextValue = {
  loaded: boolean
  loading: boolean
  error: string | null
  raw: ProcurementEffectivePermissionsDto | null
  permissions: ProcurementPermissionMap
  can: (permissionCode: ProcurementPermissionCode | string) => boolean
  canAny: (permissionCodes: Array<ProcurementPermissionCode | string>) => boolean
  canAll: (permissionCodes: Array<ProcurementPermissionCode | string>) => boolean
  getScope: (permissionCode: ProcurementPermissionCode | string) => ProcurementPermissionScope | null
  hasRouteAccess: (routeSegment: string) => boolean
  reload: () => Promise<void>
}

const defaultValue: ProcurementPermissionContextValue = {
  loaded: false,
  loading: true,
  error: null,
  raw: null,
  permissions: {},
  can: () => false,
  canAny: () => false,
  canAll: () => false,
  getScope: () => null,
  hasRouteAccess: () => false,
  reload: async () => undefined,
}

const ProcurementPermissionContext =
  createContext<ProcurementPermissionContextValue>(defaultValue)

export const useProcurementPermissions = () => useContext(ProcurementPermissionContext)

const toPermissionMap = (
  resolvedPermissions?: Record<string, {allowed?: boolean; scope?: string | null; source?: string} | undefined>
): ProcurementPermissionMap => {
  if (!resolvedPermissions) return {}
  const out: ProcurementPermissionMap = {}
  Object.entries(resolvedPermissions).forEach(([code, decision]) => {
    if (!code) return
    out[code] = {
      allowed: decision?.allowed === true,
      scope: (decision?.scope as ProcurementPermissionScope | null) ?? null,
      source: 'ROLE',
    }
  })
  return out
}

export const ProcurementPermissionProvider: FC<WithChildren> = ({children}) => {
  const {
    isSuperAdmin,
    resolvedPermissions,
    permissionsLoaded,
    hasPermission: authHasPermission,
    hasAnyPermission: authHasAnyPermission,
    currentUser,
  } = useAuth()
  const loading = !permissionsLoaded
  const loaded = permissionsLoaded
  const error: string | null = null
  const permissions = useMemo(() => toPermissionMap(resolvedPermissions), [resolvedPermissions])
  const raw = useMemo<ProcurementEffectivePermissionsDto | null>(() => {
    if (!permissionsLoaded) return null
    return {
      userId: Number(currentUser?.id ?? 0),
      roleId: currentUser?.role?.id ?? null,
      roleName: currentUser?.role?.roleName ?? null,
      permissions,
    }
  }, [permissionsLoaded, currentUser, permissions])

  const can = useCallback(
    (permissionCode: ProcurementPermissionCode | string) => {
      if (isSuperAdmin) return true
      return authHasAnyPermission(expandProcurementPermissionCandidates(String(permissionCode)))
    },
    [isSuperAdmin, authHasAnyPermission]
  )

  const canAny = useCallback(
    (permissionCodes: Array<ProcurementPermissionCode | string>) => {
      if (isSuperAdmin) return true
      const expanded = permissionCodes.flatMap((code) => expandProcurementPermissionCandidates(String(code)))
      return authHasAnyPermission(expanded)
    },
    [isSuperAdmin, authHasAnyPermission]
  )

  const canAll = useCallback(
    (permissionCodes: Array<ProcurementPermissionCode | string>) =>
      permissionCodes.every((permissionCode) => can(permissionCode)),
    [can]
  )

  const getScope = useCallback(
    (permissionCode: ProcurementPermissionCode | string): ProcurementPermissionScope | null =>
      permissions[String(permissionCode)]?.scope ?? null,
    [permissions]
  )

  const hasRouteAccess = useCallback(
    (routeSegment: string) => {
      if (isSuperAdmin) return true
      const required = getRequiredPermissionsForProcurementRoute(routeSegment)
      if (!required || required.length === 0) return true
      return canAny(required)
    },
    [isSuperAdmin, canAny]
  )

  const value = useMemo<ProcurementPermissionContextValue>(
    () => ({
      loaded,
      loading,
      error,
      raw,
      permissions,
      can,
      canAny,
      canAll,
      getScope,
      hasRouteAccess,
      reload: async () => undefined,
    }),
    [loaded, loading, error, raw, permissions, can, canAny, canAll, getScope, hasRouteAccess]
  )

  return (
    <ProcurementPermissionContext.Provider value={value}>
      {children}
    </ProcurementPermissionContext.Provider>
  )
}
